type ActivityCallStatus =
  | 'pending'
  | 'recording'
  | 'transcribing'
  | 'awaiting_summary'
  | 'summarizing'
  | 'complete'
  | 'failed'

type ActivityTodo = {
  task?: string | null
  completed?: boolean
}

export type LiverActivityKind = 'call' | 'assignment'
export type LiverActivityTone = 'sky' | 'emerald' | 'red' | 'amber' | 'slate' | 'violet'

export interface LiverActivityCall {
  id: string
  called_at: string
  duration_seconds: number | null
  status: ActivityCallStatus
  summary: string | null
  error_message?: string | null
  todos: ActivityTodo[] | null
  next_follow_date: string | null
}

export interface LiverActivityAssignment {
  id: string
  started_at: string
  ended_at: string | null
  change_reason: string | null
  handoff_notes: string | null
  manager?: {
    name?: string | null
    email?: string | null
  } | null
  predecessor?: {
    name?: string | null
    email?: string | null
  } | null
}

export interface LiverActivityItem {
  id: string
  kind: LiverActivityKind
  tone: LiverActivityTone
  occurredAt: string
  title: string
  description: string
  meta: string
  sourceId: string
}

export function buildLiverActivityItems(
  calls: LiverActivityCall[],
  assignments: LiverActivityAssignment[]
): LiverActivityItem[] {
  return [
    ...calls.map(callToActivityItem),
    ...assignments.map(assignmentToActivityItem)
  ].sort((a, b) => compareDateDesc(a.occurredAt, b.occurredAt))
}

function callToActivityItem(call: LiverActivityCall): LiverActivityItem {
  const openTodos = (call.todos ?? []).filter((todo) =>
    typeof todo.task === 'string' && todo.task.trim().length > 0 && todo.completed !== true
  )
  const metaParts = [
    formatDuration(call.duration_seconds),
    openTodos.length > 0 ? `TODO ${openTodos.length}件` : null,
    call.next_follow_date ? `次回 ${call.next_follow_date}` : null
  ].filter(Boolean)

  return {
    id: `call:${call.id}`,
    kind: 'call',
    tone: toneForCallStatus(call.status),
    occurredAt: call.called_at,
    title: `通話: ${labelForCallStatus(call.status)}`,
    description: callDescription(call),
    meta: metaParts.join(' · '),
    sourceId: call.id
  }
}

function assignmentToActivityItem(assignment: LiverActivityAssignment): LiverActivityItem {
  const managerName =
    assignment.manager?.name?.trim() ||
    assignment.manager?.email?.trim() ||
    '担当未設定'
  const predecessorName =
    assignment.predecessor?.name?.trim() ||
    assignment.predecessor?.email?.trim() ||
    null
  const meta = [
    assignment.ended_at == null ? '現担当' : '過去担当',
    predecessorName ? `前任 ${predecessorName}` : null
  ].filter(Boolean).join(' · ')

  return {
    id: `assignment:${assignment.id}`,
    kind: 'assignment',
    tone: assignment.ended_at == null ? 'amber' : 'slate',
    occurredAt: assignment.started_at,
    title: `担当変更: ${managerName}`,
    description:
      assignment.handoff_notes?.trim() ||
      assignment.change_reason?.trim() ||
      '担当履歴が更新されました。',
    meta,
    sourceId: assignment.id
  }
}

function callDescription(call: LiverActivityCall): string {
  if (call.status === 'failed') {
    return call.error_message?.trim() || '通話処理に失敗しました。'
  }
  if (call.summary?.trim()) {
    return call.summary.trim().split(/\r?\n/)[0]
  }
  switch (call.status) {
    case 'pending':
      return '文字起こし待ちです。'
    case 'recording':
      return '録音中です。'
    case 'transcribing':
      return '文字起こし中です。'
    case 'awaiting_summary':
      return '議事録生成待ちです。'
    case 'summarizing':
      return '議事録生成中です。'
    case 'complete':
      return '通話処理が完了しました。'
  }
}

function labelForCallStatus(status: ActivityCallStatus): string {
  switch (status) {
    case 'pending':
      return '待機中'
    case 'recording':
      return '録音中'
    case 'transcribing':
      return '文字起こし中'
    case 'awaiting_summary':
      return '議事録生成待ち'
    case 'summarizing':
      return '議事録生成中'
    case 'complete':
      return '完了'
    case 'failed':
      return '失敗'
  }
}

function toneForCallStatus(status: ActivityCallStatus): LiverActivityTone {
  switch (status) {
    case 'failed':
      return 'red'
    case 'complete':
      return 'emerald'
    case 'awaiting_summary':
      return 'amber'
    case 'summarizing':
      return 'violet'
    case 'transcribing':
    case 'recording':
      return 'sky'
    case 'pending':
      return 'slate'
  }
}

function formatDuration(seconds: number | null): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  if (minutes === 0) return `${rest}秒`
  return rest === 0 ? `${minutes}分` : `${minutes}分${rest}秒`
}

function compareDateDesc(left: string, right: string): number {
  return new Date(right).getTime() - new Date(left).getTime()
}
